/**
 * Rich text, kept to what terms and conditions need (BILL-015): bold,
 * italic, underline, numbered and bulleted lists, paragraphs, line breaks and
 * links. Everything else is dropped — a tag, an attribute, a script, a style —
 * so the HTML can be put on a screen and on a printed PDF as it is.
 *
 * No DOM: the same function runs in the browser editor and in the server's
 * print path, and agrees with itself in both. Text that was typed before the
 * editor existed has no tags at all; it is escaped and its line breaks kept.
 */

const ALLOWED: ReadonlySet<string> = new Set(['b', 'strong', 'i', 'em', 'u', 'ol', 'ul', 'li', 'p', 'br', 'a', 'div'])
const DROP_WITH_CONTENT = /<(script|style|iframe|object|embed|template)\b[\s\S]*?<\/\1\s*>/gi
const TAG = /<!--[\s\S]*?-->|<\/?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/g
/** Escapes text, leaving entities the browser already wrote (`&amp;`) alone. */
const escapeText = (text: string): string =>
  text.replace(/&(?!(#\d+|#x[0-9a-f]+|[a-z]+);)/gi, '&amp;')
    .replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const hrefOf = (attrs: string): string | null => {
  const m = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs)
  const raw = (m?.[1] ?? m?.[2] ?? m?.[3] ?? '').trim()
  return /^(https?:|mailto:)/i.test(raw) ? raw.replace(/"/g, '%22') : null
}

/**
 * What a printed page also keeps: terms written as HTML in a terms template
 * may lay out a table or a heading. Still no attribute but a cell's spans.
 */
const PRINT_EXTRA: ReadonlySet<string> = new Set([
  'table', 'thead', 'tbody', 'tr', 'td', 'th', 'h1', 'h2', 'h3', 'h4', 'span', 'small', 'sup', 'sub', 'hr',
])

export interface RichTextOptions {
  /** Keep the print-only tags too (tables, headings), as a terms template may use them. */
  readonly print?: boolean
}

/** Does this look like the editor's HTML rather than plain typed text? */
export const isRichText = (value: string, options: RichTextOptions = {}): boolean =>
  /<\/?(b|strong|i|em|u|ol|ul|li|p|br|a|div)\b/i.test(value)
  || (options.print === true && /<\/?(table|thead|tbody|tr|td|th|h[1-4]|span|small|sup|sub|hr)\b/i.test(value))

const spansOf = (attrs: string): string =>
  [...attrs.matchAll(/\b(colspan|rowspan)\s*=\s*["']?(\d{1,2})\b/gi)]
    .map((m) => ` ${m[1]!.toLowerCase()}="${m[2]}"`).join('')

/**
 * Rebuilds the HTML from its tags: an allowed tag is written fresh with no
 * attribute of its own, and everything else — text, a broken or unterminated
 * tag — is escaped, so nothing the input says can become markup.
 */
export const sanitizeRichText = (input: unknown, options: RichTextOptions = {}): string => {
  const value = typeof input === 'string' ? input : ''
  if (value.trim() === '') return ''
  if (!isRichText(value, options)) return escapeText(value).replace(/\r?\n/g, '<br>')

  const source = value.replace(DROP_WITH_CONTENT, '')
  let out = ''
  let last = 0
  for (const m of source.matchAll(TAG)) {
    out += escapeText(source.slice(last, m.index))
    last = (m.index ?? 0) + m[0].length
    const name = (m[1] ?? '').toLowerCase()
    const extra = options.print === true && PRINT_EXTRA.has(name)
    if (!name || (!ALLOWED.has(name) && !extra)) continue
    const closing = m[0].startsWith('</')
    if (name === 'br') { out += '<br>'; continue }
    if (name === 'hr') { if (!closing) out += '<hr>'; continue }
    if (closing) { out += `</${name}>`; continue }
    if (name === 'a') {
      const href = hrefOf(m[2] ?? '')
      out += href ? `<a href="${href}" target="_blank" rel="noopener noreferrer">` : '<a>'
      continue
    }
    out += (name === 'td' || name === 'th') ? `<${name}${spansOf(m[2] ?? '')}>` : `<${name}>`
  }
  out += escapeText(source.slice(last))
  return out
}
